<template>
    <div
        class="bg-white box-border flex flex-col items-center p-10 relative rounded-lg shadow-lg max-w-4xl w-full"
        @click.stop
        data-node-id="24:950"
    >
        <!-- ヘッダー -->
        <div class="border-b border-gray-300 box-border flex items-center justify-between py-4 w-full">
            <div class="text-xl font-light text-gray-700 whitespace-nowrap leading-7">
              動画をアップロードする
            </div>
            <button
                class="h-8 w-6 flex items-center justify-center cursor-pointer hover:opacity-70 transition-opacity duration-200"
                @click="handleClose"
                data-name="xmark-light 1"
            >
                <i class="fa-light fa-xmark text-gray-700 text-2xl"></i>
            </button>
        </div>

        <ErrorMessage :message="errorMessage" @clear-message="errorMessage = ''" />

        <!-- コンテンツエリア -->
        <div class="flex flex-col gap-6 items-center justify-center py-6 w-full" data-name="select">
            <!-- ラジオボタン -->
            <div class="flex gap-6 items-center w-full">
                <!-- YoutubeのURL -->
                <label
                    class="flex gap-2 items-center cursor-pointer"
                    data-name="radio_button_with_label"
                >
                    <input
                        type="radio"
                        v-model="uploadType"
                        value="url"
                        class="w-6 h-6 text-emerald-600 border-gray-300 focus:ring-emerald-500 cursor-pointer"
                    />
                    <span class="text-sm font-normal text-gray-700 whitespace-nowrap leading-5">
                      YoutubeのURL
                    </span>
                </label>

                <!-- 動画ファイル -->
                <label
                    class="flex gap-2 items-center cursor-pointer"
                    data-name="radio_button_with_label"
                >
                    <input
                        type="radio"
                        v-model="uploadType"
                        value="file"
                        class="w-6 h-6 text-emerald-600 border-gray-300 focus:ring-emerald-500 cursor-pointer"
                    />
                    <span class="text-sm text-gray-700 whitespace-nowrap leading-5">
                      動画ファイル
                    </span>
                </label>
            </div>

            <!-- 入力フィールド -->
            <div class="w-full">
                <!-- URL入力 -->
                <template v-if="uploadType === 'url'">
                <input
                    v-model="videoUrl"
                    type="text"
                    class="w-full border border-gray-300 box-border flex flex-col gap-1 items-start px-4 py-2 rounded text-sm text-gray-900 leading-6 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                    placeholder="https://www.youtube.com/watch?v=xxxxx"
                />
                <span class="w-full text-gray-500 text-xs font-light leading-4 break-words">
                  ※アップロードできる動画は1時間未満のものに限ります。
                </span>
                </template>

                <UploadFile
                    v-else
                    @upload-video:start="emit('upload-video:start')"
                    @upload-video:video="handleUploadVideo"
                    @upload-video:failed="handleUploadVideoFailed"
                 />
            </div>

            <!-- アップロードボタン -->
            <button
                v-if="uploadType === 'url'"
                class="btn-primary"
                :class="canUpload ? 'hover:bg-emerald-700' : 'opacity-50 cursor-not-allowed'"
                :disabled="!canUpload"
                @click="startUpload"
            >
                <span>
                  この動画をアップロード
                </span>
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, defineEmits, inject } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import UploadFile from './UploadFile.vue';
import ErrorMessage from '../ErrorMessage.vue';

const emit = defineEmits([
    'upload-video:start',
    'upload-video:failed',
    'upload-video:video',
    'close-modal',
]);
const store = useStore();
const accessToken = store.state.accessToken;
const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;
const fetchChannels = inject("fetchChannels");
const uploadType = ref("url");

const errorMessage = ref("");
const videoUrl = ref('');

// YoutubeURLデータの転送
const startUpload = async () => {
    if(uploadType.value !== 'url') return

    emit("upload-video:start");
    try {
        const uploadResponse = await axios.post(`${apiEndpoint}/upload-video/`, {
                youtube_url: videoUrl.value,
                upload_type: 'url',
            },
            {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
            }
        );
        // アップロード完了
        emit("upload-video:video", uploadResponse.data);
    } catch (error) {
        console.error("アップロードエラー:", error);
        if (error.response?.status == 403) {
            errorMessage.value = 'チャンネルに対するアクセス権がありません。チャンネル管理者にお問い合わせください。';
            fetchChannels();
        } else if (error.response && error.response.data && error.response.data.error) {
            const errorText = error.response.data.error;
            errorMessage.value = errorText;
        } else {
            errorMessage.value = '送信中にエラーが発生しました。時間を空けて再度お試しください。';
        }
        emit('upload-video:failed', errorMessage.value);
    }
};

const canUpload = computed(() => {
    return uploadType.value === 'url' && videoUrl.value.trim().length > 0;
})

const handleClose = () => {
    resetForm();
    emit('close-modal');
}

const resetForm = () => {
    videoUrl.value = '';
    emit('update:upload-type', 'url');
}

const handleUploadVideo = (video) => {
    emit("upload-video:video", video);
}

const handleUploadVideoFailed = (message) => {
    errorMessage.value = message;
    emit('upload-video:failed', message);
}
</script>
